/**
 * Product feature + overage rendering helpers (OCP-210).
 *
 * Shared by ProductsPage (catalog tiles) and SubscriptionDetailPage
 * ("What's included" section) so customers see a consistent feature
 * list across "what you'd get" and "what you have" surfaces.
 *
 * `features` and `overage_config` come from the operator-curated
 * `portal_products` table as free-form JSON. The shape varies per
 * family — for OMS today:
 *
 *   features: {
 *     kind: 'subscription' | 'topup',
 *     monthly_sends?: number,    // subs
 *     topup_sends?: number,      // top-ups
 *     tier?: 'free' | 'developer' | 'pro' | 'enterprise',
 *     ...future: dkim_slots, custom_domains, retention_days, …
 *   }
 *
 *   overage_config (subs only): {
 *     per_unit_eur_cents: number,
 *     unit_label?: string,          // default: 'email'
 *     bucket_size?: number,         // bill in N-unit increments
 *   }
 *
 * The renderers are defensive — unknown shapes degrade to a generic
 * "key: value" line rather than throwing. Operator-set
 * `marketing_bullets` always win when present; these helpers are the
 * fallback for products that haven't been hand-edited yet.
 *
 * OCP-656: the lines come from the `product-features` catalogue in the
 * active portal language; numbers and money follow the locale too.
 */
import i18n, { intlTag } from '../i18n';
import { fmtNumber } from './format-money';

const NS = 'product-features';

/**
 * Build a bullet list from a `features` object. Known keys get
 * human-readable rendering; unknown keys fall through to
 * `${humanizeKey(key)}: ${value}` so the operator at least sees
 * something while curating marketing copy.
 */
export function featuresToBullets(features: Record<string, unknown>): string[] {
  const t = i18n.getFixedT(null, NS);
  const out: string[] = [];
  const seen = new Set<string>();
  const add = (key: string, line: string) => {
    if (seen.has(key)) return;
    seen.add(key);
    out.push(line);
  };

  // ---- OMS-family known fields ------------------------------------
  if (typeof features.monthly_sends === 'number') {
    add('monthly_sends', t('monthlySends', { n: fmtNumber(features.monthly_sends) }));
  }
  if (typeof features.topup_sends === 'number') {
    add('topup_sends', t('topupSends', { n: fmtNumber(features.topup_sends) }));
  }
  if (typeof features.dkim_slots === 'number') {
    add('dkim_slots', t('dkimSlots', { count: features.dkim_slots }));
  }
  if (typeof features.custom_domains === 'number') {
    add('custom_domains', t('customDomains', { count: features.custom_domains }));
  }
  if (typeof features.retention_days === 'number') {
    add('retention_days', t('retentionDays', { n: features.retention_days }));
  }
  if (typeof features.api_keys === 'number') {
    add('api_keys', t('apiKeys', { n: features.api_keys }));
  }
  if (typeof features.webhooks === 'number') {
    add('webhooks', t('webhooks', { n: features.webhooks }));
  }
  if (typeof features.suppression_list === 'boolean' && features.suppression_list) {
    add('suppression_list', t('suppressionList'));
  }
  if (typeof features.templates === 'boolean' && features.templates) {
    add('templates', t('templates'));
  }

  // ---- Generic fallback for unknown keys --------------------------
  // Skip internal-only metadata keys that aren't useful for customers.
  const skipKeys = new Set(['kind', 'tier']);
  for (const [key, value] of Object.entries(features)) {
    if (seen.has(key) || skipKeys.has(key)) continue;
    if (value === null || value === undefined || value === '') continue;
    if (typeof value === 'boolean') {
      if (value) add(key, humanizeKey(key));
    } else if (typeof value === 'number' || typeof value === 'string') {
      add(key, `${humanizeKey(key)}: ${value}`);
    }
  }

  return out;
}

/**
 * Render an overage_config object as a single human-readable line, or
 * null when no overage is configured (e.g. free plan).
 *
 * Examples:
 *   {per_unit_eur_cents: 19, unit_label: 'email'} → "€0.19 per email beyond your monthly quota"
 *   {per_unit_eur_cents: 1, bucket_size: 1000} → "€0.01 per 1,000 units beyond your monthly quota"
 */
export function overageLine(overage: Record<string, unknown> | null): string | null {
  if (!overage) return null;
  const t = i18n.getFixedT(null, NS);
  const cents = overage.per_unit_eur_cents;
  if (typeof cents !== 'number' || cents <= 0) return null;
  const unit =
    typeof overage.unit_label === 'string' && overage.unit_label.length > 0
      ? overage.unit_label
      : t('unitEmail');
  const bucket =
    typeof overage.bucket_size === 'number' && overage.bucket_size > 1
      ? overage.bucket_size
      : 1;
  // Sub-cent rates (e.g. 0.19¢ per email) need 4 decimal places to
  // render as "€0.0019" instead of "€0.00". Whole-cent rates render
  // with 2 decimals — "€0.01" not "€0.0100".
  const subCent = cents < 1;
  const eur = new Intl.NumberFormat(intlTag(), {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: subCent ? 4 : 2,
    maximumFractionDigits: subCent ? 4 : 2,
  }).format(cents / 100);
  const unitPlural = bucket === 1 ? unit : t('unitBucket', { n: fmtNumber(bucket), unit });
  return t('overage', { price: eur, unit: unitPlural });
}

function humanizeKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
